import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { selectRole, selectIsAuthenticated } from "../../store/auth";
import { RoleUtils } from "../../constants/roles";

/**
 * Role-Based Protected Route Component
 * Protects routes based on user roles with hierarchy support
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.requiredRole - Required role for access
 * @param {boolean} props.allowHigherRoles - Allow users with higher roles (default: true)
 * @param {string} props.redirectTo - Custom redirect path for unauthorized users
 */
const RoleProtectedRoute = ({ 
  children, 
  requiredRole, 
  allowHigherRoles = true,
  redirectTo = "/login" 
}) => {
  const token = localStorage.getItem("acstkn");
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectRole);

  // Check if user is authenticated
  if (!token || !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  if (!userRole) {
    return <Navigate to="/login" replace />;
  }

  // Check role permission
  const hasAccess = allowHigherRoles 
    ? RoleUtils.hasRoleOrHigher(userRole, requiredRole)
    : RoleUtils.hasRole(userRole, requiredRole);

  if (!hasAccess) {
    return (
      <div className="w-full h-[80vh] flex flex-col items-center justify-center gap-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-red-500 mb-4">Access Denied</h2>
          <h3 className="text-xl text-red-400 mb-2">
            You don't have permission to access this area.
          </h3>
          <p className="text-gray-600 mb-6">
            Required role: <span className="font-semibold">{RoleUtils.getRoleDisplayName(requiredRole)}</span>
          </p>
          <p className="text-gray-600">
            Your role: <span className="font-semibold">{RoleUtils.getRoleDisplayName(userRole)}</span>
          </p>
        </div>
        <div className="mt-6">
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default RoleProtectedRoute;
